import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import CatalogChart, { QueryResult } from './CatalogChart';
import MultiSeriesChart, { CorrelationPanel, ComparisonResult, seriesColor } from './MultiSeriesChart';
import { CustomGraph, GraphSpec } from '../views/StoreDashboardCustomView';

interface CatalogDimension { key: string; label: string; type: 'category' | 'time' }
interface CatalogMeasure { key: string; label: string; format: string }
interface CatalogFilter { key: string; label: string; type: string; values?: string[] }
interface CatalogSource {
  key: string;
  label: string;
  kind: 'collection' | 'timeseries';
  chartTypes: string[];
  dimensions: CatalogDimension[];
  measures: CatalogMeasure[];
  filters: CatalogFilter[];
}
interface Catalog {
  sources: CatalogSource[];
  timeRanges: { key: string; label: string }[];
  buckets: string[];
}

interface CompareSeries {
  source: string;
  measure: string;
  filters: Record<string, unknown>;
  axis: 'left' | 'right';
}

const CHART_LABELS: Record<string, string> = {
  bar: 'Bar',
  stackedBar: 'Stacked bar',
  line: 'Line',
  area: 'Area',
  stackedArea: 'Stacked area',
  pie: 'Pie',
  number: 'Big number',
};

const COMPARE_CHART_TYPES = ['line', 'area', 'bar'];

const resolveFilterList = (filters: Record<string, unknown>) =>
  Object.entries(filters)
    .filter(([, value]) => value !== '' && value != null && value !== false)
    .map(([key, value]) => ({ key, value }));

export interface GraphBuilderPanelProps {
  // When set, the panel edits an existing saved graph.
  graphId?: string;
  // Called after a successful save (e.g. navigate or switch tabs).
  onSaved: () => void;
  // Label for the save button.
  saveLabel?: string;
  // Optional secondary action rendered beside Save (e.g. Cancel).
  footerExtra?: React.ReactNode;
}

const GraphBuilderPanel: React.FC<GraphBuilderPanelProps> = ({ graphId, onSaved, saveLabel = 'Save graph', footerExtra }) => {
  const editing = Boolean(graphId);

  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);

  const [mode, setMode] = useState<'single' | 'compare'>('single');

  // Single-query state.
  const [sourceKey, setSourceKey] = useState('');
  const [measure, setMeasure] = useState('');
  const [measures, setMeasures] = useState<string[]>([]);
  const [groupBy, setGroupBy] = useState('');
  const [filters, setFilters] = useState<Record<string, unknown>>({});

  // Comparison state.
  const [compareSeries, setCompareSeries] = useState<CompareSeries[]>([]);
  const [scaleMode, setScaleMode] = useState<'dual' | 'normalized'>('dual');

  // Shared.
  const [timeRange, setTimeRange] = useState('all');
  const [bucket, setBucket] = useState('month');
  const [chartType, setChartType] = useState('bar');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  const [result, setResult] = useState<QueryResult | null>(null);
  const [multiResult, setMultiResult] = useState<ComparisonResult | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const sourceByKey = useCallback(
    (key: string) => catalog?.sources.find((s) => s.key === key) || null,
    [catalog]
  );

  const source = useMemo(() => sourceByKey(sourceKey), [sourceByKey, sourceKey]);

  const applySourceDefaults = useCallback((src: CatalogSource) => {
    setChartType(src.chartTypes[0] || 'bar');
    if (src.kind === 'timeseries') {
      setMeasures(src.measures.map((m) => m.key));
      setMeasure('');
      setGroupBy('');
    } else {
      setMeasure(src.measures[0]?.key || 'count');
      setMeasures([]);
      setGroupBy('');
    }
    setFilters({});
    setTimeRange('all');
    setBucket('month');
  }, []);

  // Load catalog (and existing graph when editing).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const catRes = await axios.get(`${API_BASE_URL}/analytics/catalog`, { withCredentials: true });
        if (cancelled) return;
        const cat: Catalog = catRes.data;
        setCatalog(cat);

        if (graphId) {
          const listRes = await axios.get(`${API_BASE_URL}/analytics/graphs`, { withCredentials: true });
          const existing: CustomGraph | undefined = (listRes.data?.graphs || []).find((g: CustomGraph) => g._id === graphId);
          if (existing && !cancelled) {
            const spec = existing.spec || ({} as GraphSpec);
            setTitle(existing.title || '');
            setDescription(existing.description || '');
            setChartType(existing.chartType || 'bar');
            setTimeRange(spec.timeRange || 'all');
            setBucket(spec.bucket || 'month');
            if (Array.isArray(spec.series) && spec.series.length) {
              setMode('compare');
              setScaleMode((spec.scaleMode as 'dual' | 'normalized') || 'dual');
              setCompareSeries(
                spec.series.map((s) => ({
                  source: s.source,
                  measure: s.measure as string,
                  filters: Object.fromEntries((s.filters || []).map((f) => [f.key, f.value])),
                  axis: (s.axis as 'left' | 'right') || 'left',
                }))
              );
            } else {
              setMode('single');
              setSourceKey(spec.source || '');
              setMeasure(spec.measure || '');
              setMeasures(spec.measures || []);
              setGroupBy(spec.groupBy || '');
              setFilters(Object.fromEntries((spec.filters || []).map((f) => [f.key, f.value])));
            }
          }
        } else {
          const first = cat.sources[0];
          if (first) {
            setSourceKey(first.key);
            applySourceDefaults(first);
          }
        }
        if (!cancelled) setHydrated(true);
      } catch (err: any) {
        if (!cancelled) setCatalogError(err.response?.data?.message || 'Failed to load catalog');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [graphId, applySourceDefaults]);

  const groupDim = source?.dimensions.find((d) => d.key === groupBy) || null;

  // Single-query spec.
  const singleSpec = useMemo<GraphSpec | null>(() => {
    if (!source) return null;
    if (source.kind === 'timeseries') {
      return {
        source: source.key,
        measures: measures.length ? measures : source.measures.map((m) => m.key),
        timeRange,
      };
    }
    return {
      source: source.key,
      measure: measure || 'count',
      groupBy: groupBy || undefined,
      filters: resolveFilterList(filters),
      timeRange,
      ...(groupDim?.type === 'time' ? { bucket } : {}),
    };
  }, [source, measure, measures, groupBy, groupDim, filters, timeRange, bucket]);

  // Comparison spec.
  const comparePayload = useMemo(() => {
    const series = compareSeries
      .filter((cs) => cs.source && cs.measure)
      .map((cs) => ({
        source: cs.source,
        measure: cs.measure,
        filters: resolveFilterList(cs.filters),
        axis: cs.axis,
      }));
    return { series, timeRange, bucket };
  }, [compareSeries, timeRange, bucket]);

  // Live preview.
  const singleKey = JSON.stringify(mode === 'single' ? singleSpec : null);
  const multiKey = JSON.stringify(mode === 'compare' ? comparePayload : null);
  useEffect(() => {
    if (mode !== 'single' || !singleSpec) return;
    let cancelled = false;
    axios
      .post(`${API_BASE_URL}/analytics/query`, singleSpec, { withCredentials: true })
      .then((res) => !cancelled && (setResult(res.data), setPreviewError(null)))
      .catch((err) => !cancelled && (setPreviewError(err.response?.data?.message || 'Query failed'), setResult(null)));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleKey, mode]);

  useEffect(() => {
    if (mode !== 'compare') return;
    if (!comparePayload.series.length) {
      setMultiResult(null);
      return;
    }
    let cancelled = false;
    axios
      .post(`${API_BASE_URL}/analytics/query-multi`, comparePayload, { withCredentials: true })
      .then((res) => !cancelled && (setMultiResult(res.data), setPreviewError(null)))
      .catch((err) => !cancelled && (setPreviewError(err.response?.data?.message || 'Comparison failed'), setMultiResult(null)));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multiKey, mode]);

  const handleModeChange = (next: 'single' | 'compare') => {
    setMode(next);
    setPreviewError(null);
    if (next === 'compare' && compareSeries.length === 0 && catalog) {
      const first = catalog.sources[0];
      setCompareSeries([{ source: first.key, measure: first.measures[0]?.key || 'count', filters: {}, axis: 'left' }]);
      if (!COMPARE_CHART_TYPES.includes(chartType)) setChartType('line');
    }
  };

  const handleSourceChange = (key: string) => {
    setSourceKey(key);
    const src = sourceByKey(key);
    if (src) applySourceDefaults(src);
  };

  const updateSeries = (index: number, patch: Partial<CompareSeries>) => {
    setCompareSeries((current) => current.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  };

  const addSeries = () => {
    if (!catalog) return;
    const first = catalog.sources[0];
    setCompareSeries((current) => [
      ...current,
      { source: first.key, measure: first.measures[0]?.key || 'count', filters: {}, axis: current.length % 2 === 0 ? 'left' : 'right' },
    ]);
  };

  const removeSeries = (index: number) => {
    setCompareSeries((current) => current.filter((_, i) => i !== index));
  };

  const save = async () => {
    const spec =
      mode === 'compare'
        ? { series: comparePayload.series, timeRange, bucket, scaleMode }
        : singleSpec;
    if (!spec || (mode === 'compare' && !comparePayload.series.length)) return;
    setSaving(true);
    const body = {
      title: title.trim() || 'Untitled graph',
      description: description.trim(),
      chartType,
      spec,
    };
    try {
      if (editing) {
        await axios.put(`${API_BASE_URL}/analytics/graphs/${graphId}`, body, { withCredentials: true });
      } else {
        await axios.post(`${API_BASE_URL}/analytics/graphs`, body, { withCredentials: true });
      }
      onSaved();
    } catch (err: any) {
      setPreviewError(err.response?.data?.message || 'Failed to save graph');
    } finally {
      setSaving(false);
    }
  };

  if (catalogError) {
    return <div className="alert alert-danger">{catalogError}</div>;
  }

  if (!catalog || !hydrated) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  const chartTypeOptions = mode === 'compare' ? COMPARE_CHART_TYPES : source?.chartTypes || [];

  const renderFilters = (defs: CatalogFilter[], values: Record<string, unknown>, onChange: (key: string, v: unknown) => void) =>
    defs.map((f) => (
      <div key={f.key} className="mb-2">
        {f.type === 'preset' ? (
          <label className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              checked={Boolean(values[f.key])}
              onChange={(e) => onChange(f.key, e.target.checked)}
            />
            <span className="form-check-label ms-1">{f.label}</span>
          </label>
        ) : f.type === 'stage' && f.values ? (
          <>
            <span className="small text-muted">{f.label}</span>
            <select
              className="form-select form-select-sm"
              value={(values[f.key] as string) || ''}
              onChange={(e) => onChange(f.key, e.target.value)}
            >
              <option value="">Any</option>
              {f.values.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </>
        ) : (
          <>
            <span className="small text-muted">{f.label}</span>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder={`Filter by ${f.label.toLowerCase()}`}
              value={(values[f.key] as string) || ''}
              onChange={(e) => onChange(f.key, e.target.value)}
            />
          </>
        )}
      </div>
    ));

  return (
    <>
      <div className="alert alert-warning fw-semibold" role="alert">
        ⚠️ These controls are still in development — the data and visualizations may not be accurate.
      </div>

      <div className="store-dash-ranges mb-3">
        <button
          type="button"
          className={`store-dash-range${mode === 'single' ? ' is-active' : ''}`}
          onClick={() => handleModeChange('single')}
        >
          Single metric
        </button>
        <button
          type="button"
          className={`store-dash-range${mode === 'compare' ? ' is-active' : ''}`}
          onClick={() => handleModeChange('compare')}
        >
          Compare (correlate)
        </button>
      </div>

      <div className="store-dash-builder" style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Library / controls */}
        <div className="card" style={{ flex: '1 1 340px', minWidth: '300px' }}>
          <div className="card-body">
            <h5 className="card-title">Data library</h5>

            {mode === 'single' ? (
              <>
                <label className="form-label mt-2">Source</label>
                <select className="form-select" value={sourceKey} onChange={(e) => handleSourceChange(e.target.value)}>
                  {catalog.sources.map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.label}
                    </option>
                  ))}
                </select>

                {source && source.kind === 'timeseries' ? (
                  <>
                    <label className="form-label mt-3">Measures</label>
                    <div className="d-flex flex-column gap-1">
                      {source.measures.map((m) => (
                        <label key={m.key} className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={measures.includes(m.key)}
                            onChange={() =>
                              setMeasures((cur) => (cur.includes(m.key) ? cur.filter((x) => x !== m.key) : [...cur, m.key]))
                            }
                          />
                          <span className="form-check-label ms-1">{m.label}</span>
                        </label>
                      ))}
                    </div>
                  </>
                ) : source ? (
                  <>
                    <label className="form-label mt-3">Measure</label>
                    <select className="form-select" value={measure} onChange={(e) => setMeasure(e.target.value)}>
                      {source.measures.map((m) => (
                        <option key={m.key} value={m.key}>
                          {m.label}
                        </option>
                      ))}
                    </select>

                    <label className="form-label mt-3">Group by</label>
                    <select className="form-select" value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
                      <option value="">None (single total)</option>
                      {source.dimensions.map((d) => (
                        <option key={d.key} value={d.key}>
                          {d.label}
                          {d.type === 'time' ? ' (over time)' : ''}
                        </option>
                      ))}
                    </select>

                    {groupDim?.type === 'time' && (
                      <>
                        <label className="form-label mt-3">Bucket</label>
                        <select className="form-select" value={bucket} onChange={(e) => setBucket(e.target.value)}>
                          {catalog.buckets.map((b) => (
                            <option key={b} value={b}>
                              {b.charAt(0).toUpperCase() + b.slice(1)}
                            </option>
                          ))}
                        </select>
                      </>
                    )}

                    {source.filters.length > 0 && (
                      <>
                        <label className="form-label mt-3">Filters</label>
                        {renderFilters(source.filters, filters, (k, v) => setFilters((cur) => ({ ...cur, [k]: v })))}
                      </>
                    )}
                  </>
                ) : null}
              </>
            ) : (
              <>
                <p className="small text-muted">Add series to overlay on a shared time axis and spot correlations.</p>
                {compareSeries.map((cs, index) => {
                  const src = sourceByKey(cs.source);
                  return (
                    <div key={index} className="border rounded p-2 mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="badge" style={{ background: seriesColor(index) }}>
                          Series {index + 1}
                        </span>
                        {compareSeries.length > 1 && (
                          <button type="button" className="btn btn-link btn-sm text-danger p-0" onClick={() => removeSeries(index)}>
                            Remove
                          </button>
                        )}
                      </div>
                      <select
                        className="form-select form-select-sm mb-1"
                        value={cs.source}
                        onChange={(e) => {
                          const next = sourceByKey(e.target.value);
                          updateSeries(index, {
                            source: e.target.value,
                            measure: next?.measures[0]?.key || 'count',
                            filters: {},
                          });
                        }}
                      >
                        {catalog.sources.map((s) => (
                          <option key={s.key} value={s.key}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                      <select
                        className="form-select form-select-sm mb-1"
                        value={cs.measure}
                        onChange={(e) => updateSeries(index, { measure: e.target.value })}
                      >
                        {(src?.measures || []).map((m) => (
                          <option key={m.key} value={m.key}>
                            {m.label}
                          </option>
                        ))}
                      </select>
                      {scaleMode === 'dual' && (
                        <select
                          className="form-select form-select-sm mb-1"
                          value={cs.axis}
                          onChange={(e) => updateSeries(index, { axis: e.target.value as 'left' | 'right' })}
                        >
                          <option value="left">Left axis</option>
                          <option value="right">Right axis</option>
                        </select>
                      )}
                      {src && src.filters.length > 0 && (
                        <div className="mt-1">
                          {renderFilters(src.filters, cs.filters, (k, v) =>
                            updateSeries(index, { filters: { ...cs.filters, [k]: v } })
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                {compareSeries.length < 6 && (
                  <button type="button" className="btn btn-outline-primary btn-sm w-100" onClick={addSeries}>
                    + Add series
                  </button>
                )}

                <label className="form-label mt-3">Scale</label>
                <select className="form-select" value={scaleMode} onChange={(e) => setScaleMode(e.target.value as 'dual' | 'normalized')}>
                  <option value="dual">Dual axis (real values)</option>
                  <option value="normalized">Normalized (compare shapes)</option>
                </select>

                <label className="form-label mt-3">Bucket</label>
                <select className="form-select" value={bucket} onChange={(e) => setBucket(e.target.value)}>
                  {catalog.buckets.map((b) => (
                    <option key={b} value={b}>
                      {b.charAt(0).toUpperCase() + b.slice(1)}
                    </option>
                  ))}
                </select>
              </>
            )}

            <label className="form-label mt-3">Time range</label>
            <select className="form-select" value={timeRange} onChange={(e) => setTimeRange(e.target.value)}>
              {catalog.timeRanges.map((r) => (
                <option key={r.key} value={r.key}>
                  {r.label}
                </option>
              ))}
            </select>

            {chartTypeOptions.length > 0 && (
              <>
                <label className="form-label mt-3">Chart type</label>
                <select className="form-select" value={chartType} onChange={(e) => setChartType(e.target.value)}>
                  {chartTypeOptions.map((c) => (
                    <option key={c} value={c}>
                      {CHART_LABELS[c] || c}
                    </option>
                  ))}
                </select>
              </>
            )}
          </div>
        </div>

        {/* Preview + meta */}
        <div className="card" style={{ flex: '2 1 460px', minWidth: '320px' }}>
          <div className="card-body">
            <label className="form-label">Title</label>
            <input
              type="text"
              className="form-control"
              placeholder="Untitled graph"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <label className="form-label mt-2">Description</label>
            <input
              type="text"
              className="form-control"
              placeholder="Optional"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />

            <hr />

            <div className="store-dash-chart-head">
              <h2>Preview</h2>
            </div>
            {previewError ? (
              <div className="alert alert-warning mb-0">{previewError}</div>
            ) : mode === 'compare' ? (
              multiResult ? (
                <>
                  <MultiSeriesChart result={multiResult} chartType={chartType} scaleMode={scaleMode} height={320} />
                  <CorrelationPanel result={multiResult} />
                </>
              ) : (
                <div className="text-muted py-5 text-center">Add a series to preview.</div>
              )
            ) : result ? (
              <CatalogChart result={result} chartType={chartType} height={320} />
            ) : (
              <div className="d-flex justify-content-center py-5">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-end align-items-center gap-2 mt-3">
        {footerExtra}
        <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : saveLabel}
        </button>
      </div>
    </>
  );
};

export default GraphBuilderPanel;
